import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import ProfessionalsAPI from '../../api/professionals';
import { extractRecord, extractList } from '../utils/appointmentEnvelope';
import {
  createAccountContextGuard,
  createAccountRequestGuard,
  invalidateAccountRequestContext,
} from './utils/accountRequestGuard';
import { createAbortableRequestTracker } from './utils/abortableRequestTracker';
import { createOperationRequestTracker } from './utils/operationRequestTracker';

const getRequestGuard = createAccountRequestGuard();
const mutationContextGuard = createAccountContextGuard();
const readRequestTracker = createAbortableRequestTracker();
const mutationRequestTracker = createOperationRequestTracker();

const normalizeMemberPayload = payload =>
  payload && typeof payload === 'object' ? payload : { id: payload };

const beginMutationRequest = (
  localState,
  rootGetters,
  { accountId, generation, group, identity }
) => {
  const contextRequest = mutationContextGuard.begin(localState, {
    accountId: accountId ?? rootGetters.getCurrentAccountId,
    generation,
  });
  const isCurrentContext = () =>
    mutationContextGuard.isCurrent(localState, contextRequest, rootGetters);

  if (!isCurrentContext()) {
    return {
      shouldStart: false,
      isCurrent: () => false,
      finish: () => false,
    };
  }

  const operationRequest = mutationRequestTracker.begin(localState, {
    group,
    identity,
  });

  return {
    accountId: contextRequest.accountId,
    shouldStart: true,
    isCurrent: () =>
      isCurrentContext() &&
      mutationRequestTracker.isLatest(localState, operationRequest),
    finish: () => {
      const completion = mutationRequestTracker.finish(
        localState,
        operationRequest
      );
      return (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        isCurrentContext()
      );
    },
  };
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchError: false,
  },
};

export const getters = {
  getProfessionals(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getProfessionalById: _state => id => {
    return _state.records.find(record => record.id === Number(id)) || {};
  },
};

export const actions = {
  get: async function getProfessionals(
    { commit, state: localState = state, rootGetters = {} },
    { accountId, generation } = {}
  ) {
    const requestedAccountId = accountId ?? rootGetters.getCurrentAccountId;
    const initialContext = mutationContextGuard.begin(localState, {
      accountId: requestedAccountId,
      generation,
    });
    if (
      !mutationContextGuard.isCurrent(localState, initialContext, rootGetters)
    ) {
      return [];
    }

    const request = getRequestGuard.begin(localState, {
      accountId: requestedAccountId,
      generation,
    });
    const abortableRequest = readRequestTracker.begin(localState, 'list');
    const isCurrentRequest = () =>
      getRequestGuard.isCurrent(localState, request, rootGetters) &&
      readRequestTracker.isCurrent(localState, abortableRequest);
    commit(types.SET_PROFESSIONAL_UI_FLAG, {
      isFetching: true,
      isFetchError: false,
    });
    try {
      const response = await ProfessionalsAPI.get(request.accountId, {
        signal: abortableRequest.signal,
      });
      const records = extractList(response);
      if (!isCurrentRequest()) return [];

      commit(types.SET_PROFESSIONALS, records);
      return records;
    } catch (error) {
      if (isCurrentRequest()) {
        commit(types.SET_PROFESSIONAL_UI_FLAG, { isFetchError: true });
        throw error;
      }
      return [];
    } finally {
      if (isCurrentRequest()) {
        commit(types.SET_PROFESSIONAL_UI_FLAG, { isFetching: false });
      }
      readRequestTracker.finish(localState, abortableRequest);
    }
  },

  create: async function createProfessional(
    { commit, state: localState = state, rootGetters = {} },
    professionalData = {}
  ) {
    const { accountId, generation, ...apiData } = professionalData;
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isCreating',
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_PROFESSIONAL_UI_FLAG, { isCreating: true });
    try {
      const response = await ProfessionalsAPI.create(
        apiData,
        request.accountId
      );
      const record = extractRecord(response);
      if (!request.isCurrent()) return undefined;

      commit(types.ADD_PROFESSIONAL, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_PROFESSIONAL_UI_FLAG, { isCreating: false });
      }
    }
  },

  update: async function updateProfessional(
    { commit, state: localState = state, rootGetters = {} },
    { id, accountId, generation, ...updateData } = {}
  ) {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_PROFESSIONAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await ProfessionalsAPI.update(
        id,
        updateData,
        request.accountId
      );
      const record = extractRecord(response);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_PROFESSIONAL, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_PROFESSIONAL_UI_FLAG, { isUpdating: false });
      }
    }
  },

  delete: async function deleteProfessional(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isDeleting',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_PROFESSIONAL_UI_FLAG, { isDeleting: true });
    try {
      await ProfessionalsAPI.delete(id, request.accountId);
      if (!request.isCurrent()) return undefined;

      commit(types.DELETE_PROFESSIONAL, id);
      return true;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_PROFESSIONAL_UI_FLAG, { isDeleting: false });
      }
    }
  },
};

export const mutations = {
  RESET_ACCOUNT_DATA(_state) {
    invalidateAccountRequestContext(_state);
    readRequestTracker.reset(_state);
    mutationRequestTracker.reset(_state);
    _state.records = [];
    _state.uiFlags = {
      isFetching: false,
      isFetchingItem: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetchError: false,
    };
  },

  [types.SET_PROFESSIONAL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_PROFESSIONALS]: MutationHelpers.set,
  [types.ADD_PROFESSIONAL]: MutationHelpers.create,
  [types.EDIT_PROFESSIONAL]: MutationHelpers.update,
  [types.DELETE_PROFESSIONAL]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
